import type { AssetsAssetSchemaTypedWithId } from '@iconik-io/iconik-api/src/services/assets';
import {
  OBJECT_TYPE_ASSETS,
  OBJECT_TYPE_COLLECTIONS,
} from '@iconik-io/iconik-api/src/types/object_types';
import type { AssetEntityNormalized } from '@iconik-io/shared-lib/src/redux/entities/normalized/asset';
import type { CollectionEntityNormalized } from '@iconik-io/shared-lib/src/redux/entities/normalized/collection';
import { unregisterFile } from '@iconik-io/shared-lib/src/uploader/file_registry';
import { isIgnoredFilePath } from '@iconik-io/uploader/src/utils/is_ignored_file_path';

import { UPLOADS_PANEL } from '../../../constants/data';
import { ONBOARDING_MODAL } from '../../../constants/modals';
import {
  DASHBOARD_ROUTE,
  SEARCH_PAGE_ROUTE,
  UPLOAD_PAGE_ROUTE,
} from '../../../constants/routes';
import { getEntity } from '../../../panels/uploads/helpers';
import { selectEntityType } from '../../../panels/uploads/selectors';
import {
  getCurrentRouteParams,
  getCurrentRoutePattern,
} from '../../../router/current_route_params';
import type { AppDispatch, GetState } from '../../../store/types';
import { historyPush } from '../../history/actions';
import { openPanel } from '../../panels/slice';
import { addNewAssetUploads } from '../../uploader/engine/thunks';
import { deleteFileComponents } from '../../uploader/file_components';
import {
  tryUploadToAsset,
  tryUploadToCollection,
  tryUploadToPlaceholder,
} from '../../uploader/thunks';
import type { PickedFile } from '../../uploader/types';

const isIgnoredFile = (file: PickedFile): boolean =>
  isIgnoredFilePath(file.fullpath?.replace(/^\//, '') ?? file.name);

export const handleFilesToUpload =
  (
    droppedFiles: PickedFile[],
    initialEntityType?: string,
    initialEntity?: AssetEntityNormalized | CollectionEntityNormalized,
    getEntitiesFromState?: boolean,
  ) =>
  (dispatch: AppDispatch, getState: GetState) => {
    const files = droppedFiles.filter((file) => {
      if (isIgnoredFile(file)) {
        unregisterFile(file.fileRefId);
        deleteFileComponents(file.fileRefId);
        return false;
      }
      return true;
    });
    if (files.length === 0) {
      return;
    }

    let entity = initialEntity;
    let entityType = initialEntityType;

    if (!entityType && !entity && getEntitiesFromState) {
      const { id: routeId, versionId: routeVersionId } =
        getCurrentRouteParams();
      entity = getEntity(getState(), routeId);
      entityType = selectEntityType(
        getState(),
        routeId,
        routeVersionId,
        getCurrentRoutePattern(),
      );
    }

    // upload to collection
    if (entity && entityType === OBJECT_TYPE_COLLECTIONS) {
      dispatch(tryUploadToCollection(entity.id, files));
      return;
    }
    // upload to placeholder
    const isPlaceholder =
      entity !== undefined && 'type' in entity && entity.type === 'PLACEHOLDER';

    if (entity && isPlaceholder) {
      dispatch(tryUploadToPlaceholder(entity.id, files));
      return;
    }

    // upload to asset
    if (entity && entityType === OBJECT_TYPE_ASSETS && !isPlaceholder) {
      // biome-ignore lint/plugin: the entity is known to be an asset here, which the entity map types as either.
      dispatch(tryUploadToAsset(entity as AssetsAssetSchemaTypedWithId, files));
      return;
    }

    // upload new assets
    if (getState().routeParams.route !== UPLOAD_PAGE_ROUTE) {
      dispatch(openPanel(UPLOADS_PANEL));
    }

    dispatch(addNewAssetUploads(files));

    const route = getState().routeParams.route;
    const isOnboardingModalOpen = Boolean(
      getState().modalsManager[ONBOARDING_MODAL]?.isOpen,
    );

    if (
      ![SEARCH_PAGE_ROUTE, UPLOAD_PAGE_ROUTE].includes(route) &&
      !(route === DASHBOARD_ROUTE && isOnboardingModalOpen)
    ) {
      dispatch(historyPush(SEARCH_PAGE_ROUTE));
    }
  };
